/**
 * PendingPmApplicationsBanner — A132 admin visibility signal.
 *
 * Shows a high-visibility banner on the Admin Dashboard when one or
 * more Property Manager applications are sitting in the `pending`
 * state. Hides itself entirely when the count is zero so the
 * dashboard stays calm when there's nothing to action.
 *
 * Permissions
 * -----------
 * The component calls the admin-only metrics endpoint
 * (/api/admin/property-manager-applications/metrics) which itself
 * enforces `require_admin`. Non-admin viewers therefore receive 401/403
 * from the API and we render nothing — no UI-side leak.
 *
 * Why a separate component (not inline on Dashboard.jsx)?
 * - reusable on the Partners page header strip if we ever want it there
 * - keeps the dashboard JSX scannable
 * - testable in isolation by data-testid
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export function PendingPmApplicationsBanner() {
  const { user } = useAuth();
  const [pending, setPending] = useState(0);
  const [ready, setReady] = useState(false);

  // Only fetch for admins. Lab techs / customers / PMs etc. should
  // never see this widget even if the page accidentally renders.
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) { setReady(true); return; }
    let cancelled = false;
    api.get("/admin/property-manager-applications/metrics")
      .then(({ data }) => {
        if (cancelled) return;
        setPending(Number(data?.pending) || 0);
        setReady(true);
      })
      .catch(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin || !ready || pending <= 0) return null;

  const label = pending === 1
    ? "Property Manager application needs review"
    : "Property Manager applications need review";

  return (
    <div
      data-testid="pending-pm-apps-banner"
      className="mb-6 rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-5 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
            <Inbox className="w-4 h-4" />
            <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-[20px] px-1 items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold ring-2 ring-white">
              <span data-testid="pending-pm-apps-banner-count">{pending}</span>
            </span>
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
              Action required
            </div>
            <div className="font-display text-lg sm:text-xl font-semibold text-slate-900 mt-0.5">
              {label} · {pending} pending
            </div>
            <p className="text-sm text-slate-600 mt-1">
              New agencies are waiting for approval to access the Property Manager Portal.
            </p>
          </div>
        </div>
        <Link
          to="/admin/partners/applications"
          data-testid="pending-pm-apps-banner-cta"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm px-5 py-2.5 shadow transition-colors whitespace-nowrap"
        >
          Review applications
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

export default PendingPmApplicationsBanner;
